import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificacaoService } from 'app/services/notificacao.service';
import { LembreteService } from 'app/services/lembrete.service';
import { ClienteService } from 'app/services/cliente.service';
import { Cliente } from 'app/models/cliente.model';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'app-lembrete-form',
    templateUrl: './lembrete-form.component.html',
    styleUrls: ['./lembrete-form.component.css'],
    standalone: false
})
export class LembreteFormComponent implements OnInit {
    @Input() lembreteId: number;
    @Input() clienteIdPreSelecionado: number = 0;

    loading: boolean;
    lembreteForm: FormGroup;

    todosClientes: Cliente[] = [];
    termoBuscaCliente: FormControl = new FormControl('');
    clientesPesquisados: Observable<Cliente[]>;

    constructor(
        private activeModal: NgbActiveModal,
        private notificacaoService: NotificacaoService,
        private lembreteService: LembreteService,
        private clienteService: ClienteService,
        private formBuilder: FormBuilder
    ) {
        this.lembreteForm = this.formBuilder.group({
            id: 0,
            clienteId: [null],
            titulo: [],
            descricao: [null],
            dataLembrete: [],
            recorrente: [false],
            intervaloEmDias: [null]
        });
    }

    async ngOnInit() {
        this.loading = true;

        await this.clienteService.obterTodosClientes(true, { pagina: 1, tamanhoPagina: 1000, ordenarPor: 'Id', descendente: false })
            .then(resposta => this.todosClientes = resposta.itens);

        this.clientesPesquisados = this.termoBuscaCliente.valueChanges.pipe(
            startWith(''),
            map(cliente => {
                if (typeof cliente === 'string') {
                    this.lembreteForm.controls['clienteId'].setValue(null);
                }
                const name = typeof cliente === 'string' ? cliente : cliente?.nome;
                return name ? this.filtrarClientes(name as string) : this.todosClientes.slice();
            }),
        );

        // Pre-select client if provided
        if (this.clienteIdPreSelecionado && this.clienteIdPreSelecionado !== 0) {
            const clientePre = this.todosClientes.find(c => c.id === this.clienteIdPreSelecionado);
            if (clientePre) {
                this.termoBuscaCliente.setValue(clientePre);
                this.lembreteForm.controls['clienteId'].setValue(clientePre.id);
            }
        }

        if (this.lembreteId && this.lembreteId != 0) {
            await this.buscarLembrete();
        }

        this.loading = false;
    }

    private filtrarClientes(value: string): Cliente[] {
        const filterValue = value.toLowerCase();
        return this.todosClientes.filter(cliente => cliente.nome.toLowerCase().includes(filterValue));
    }

    nomeExibicaoAutocomplete(cliente: Cliente): string {
        return cliente && cliente.nome ? cliente.nome : '';
    }

    selecionarCliente(event: any): void {
        const clienteId = event.option.value.id;
        this.lembreteForm.controls['clienteId'].setValue(clienteId);
    }

    async buscarLembrete() {
        const lembrete = await this.lembreteService.obter(this.lembreteId);

        this.lembreteForm = this.formBuilder.group({
            id: lembrete.id,
            clienteId: lembrete.clienteId,
            titulo: lembrete.titulo,
            descricao: lembrete.descricao,
            dataLembrete: lembrete.dataLembrete ? new Date(lembrete.dataLembrete).toISOString().substring(0, 10) : null,
            recorrente: [lembrete.recorrente || false],
            intervaloEmDias: [lembrete.intervaloEmDias || null]
        });

        // Set the client autocomplete value if there's a client
        if (lembrete.clienteId) {
            const cliente = this.todosClientes.find(c => c.id === lembrete.clienteId);
            if (cliente) {
                this.termoBuscaCliente.setValue(cliente);
            }
        }

        // Re-bind the autocomplete pipe
        this.clientesPesquisados = this.termoBuscaCliente.valueChanges.pipe(
            startWith(''),
            map(c => {
                if (typeof c === 'string') {
                    this.lembreteForm.controls['clienteId'].setValue(null);
                }
                const name = typeof c === 'string' ? c : c?.nome;
                return name ? this.filtrarClientes(name as string) : this.todosClientes.slice();
            }),
        );
    }

    public cancelar() {
        this.activeModal.close(false);
    }

    async confirmar() {
        if (!this.lembreteForm.valid) {
            this.notificacaoService.mensagemErro("Preencha corretamente todos os campos do formulário");
            return;
        }

        if (!this.lembreteId || this.lembreteId === 0)
            await this.adicionar();
        else
            await this.atualizar();
    }

    private montarLembrete() {
        return {
            id: this.lembreteForm.value.id,
            clienteId: this.lembreteForm.value.clienteId || undefined,
            titulo: this.lembreteForm.value.titulo,
            descricao: this.lembreteForm.value.descricao,
            dataLembrete: this.lembreteForm.value.dataLembrete,
            recorrente: this.lembreteForm.value.recorrente || false,
            intervaloEmDias: this.lembreteForm.value.recorrente ? this.lembreteForm.value.intervaloEmDias : undefined
        };
    }

    private async adicionar() {
        this.loading = true;
        try {
            const resposta = await this.lembreteService.adicionar(this.montarLembrete());
            this.notificacaoService.mensagemSucesso(resposta.mensagem);
            this.activeModal.close(true);
        } catch (err: any) {
            this.notificacaoService.mensagemErro(err?.error?.mensagem || err?.error?.message || 'Erro ao adicionar lembrete');
        } finally {
            this.loading = false;
        }
    }

    private async atualizar() {
        this.loading = true;
        try {
            const resposta = await this.lembreteService.atualizar(this.montarLembrete());
            this.notificacaoService.mensagemSucesso(resposta.mensagem);
            this.activeModal.close(true);
        } catch (err: any) {
            this.notificacaoService.mensagemErro(err?.error?.mensagem || err?.error?.message || 'Erro ao atualizar lembrete');
        } finally {
            this.loading = false;
        }
    }
}
